import {
  ASSESSMENT_THREADS,
  ASSESSMENTS,
  AssessmentSnippet,
  getFullName,
  getInitials,
  MessageThread,
  PATIENT_THREADS,
  ThreadType,
} from '@hb/shared'
import { useMemo } from 'react'

import { useCachedPractice, useCachedUser } from '../../collections/hooks/cached'
import { useApp } from '../../contexts/AppContext'
import { useFetchData } from '../../contexts/db/FetchDataContext'

export const useThreadTitle = (
  uid: string | null,
  threadType: ThreadType,
  threadId: string | undefined,
  thread: MessageThread | null,
) => {
  const { appName } = useApp()
  const { useDocument } = useFetchData()
  const directMessageUserId = useMemo(() => {
    if (!thread) return null
    if (thread.type === ThreadType.DIRECT || (thread?.subscribers?.length ?? 0) < 3)
      return thread?.subscribers?.find(m => m !== uid) ?? null
    return null
  }, [thread, uid])
  const { data: directMessageUser } = useCachedUser(directMessageUserId)
  const { data: assessmentThreadAssessment } = useDocument<AssessmentSnippet>(
    ASSESSMENTS,
    threadType === ThreadType.ASSESSMENT ? threadId : null,
  )

  const midwifeId = useMemo(
    () => assessmentThreadAssessment?.midwifeId ?? null,
    [assessmentThreadAssessment],
  )

  const { data: midwife } = useCachedPractice(midwifeId)

  const patientId = useMemo(() => {
    if (threadType === ThreadType.ASSESSMENT) return assessmentThreadAssessment?.patientId ?? null
    if (threadType === ThreadType.PATIENT) return threadId ?? null
    return null
  }, [threadType, threadId, assessmentThreadAssessment])
  const { data: patient } = useCachedUser(patientId)

  const initials = useMemo(() => {
    switch (threadType) {
      case ThreadType.DIRECT:
        return directMessageUser ? getInitials(getFullName(directMessageUser)) : ''
      case ThreadType.ASSESSMENT:
      case ThreadType.PATIENT:
        if (uid === threadId && threadType === ThreadType.PATIENT) return 'HB'
        return patient ? getInitials(getFullName(patient)) : ''
      case ThreadType.GROUP:
        if (directMessageUser) return getInitials(getFullName(directMessageUser))
        return getInitials(thread?.title ?? '')?.slice(0, 3) || 'G'
      default:
        return ''
    }
  }, [thread, directMessageUser, patient, threadType, uid, threadId])
  const title = useMemo(() => {
    if (threadType === ThreadType.DIRECT)
      return directMessageUser ? getFullName(directMessageUser) : ''
    if (thread?.title) return thread?.title
    switch (threadType) {
      case ThreadType.ASSESSMENT:
        return appName === 'providers-app'
          ? 'Chat with Hamilton Billing'
          : `Chat with ${midwife?.name ?? 'Practice'}`
      case ThreadType.PATIENT:
        if (uid === threadId && threadType === ThreadType.PATIENT)
          return 'Chat with Hamilton Billing'
        return patient ? getFullName(patient) : ''
      case ThreadType.GROUP:
        return (thread?.subscribers?.length ?? 0) < 3
          ? getFullName(directMessageUser)
          : 'Group Chat'
      default:
        return ''
    }
  }, [thread, directMessageUser, patient, threadType, uid, threadId, appName, midwife])

  return {
    title,
    directMessageUser,
    directMessageUserId,
    initials,
  }
}

export const useAssessmentThread = (assessmentId: string) => {
  const { useDocument } = useFetchData()
  return useDocument<MessageThread>(ASSESSMENT_THREADS, assessmentId)
}

export const usePatientThread = (patientId: string | null) => {
  const { useDocument } = useFetchData()
  return useDocument<MessageThread>(PATIENT_THREADS, patientId)
}
